import { Color } from './color';
import { Feature, FeatureType } from './features';

// Notification `category` strings come from two sources with no shared enum: legacy rows
// carry a raw DB value (INFO/ALERT/GENERAL/PLATFORM/...), new inbox rows derive an
// uppercased module prefix from the notification key client-side (see
// apps/hela/src/utils/inbox.ts's categoryFromKey — case.expiry -> "CASE", comply.* ->
// "AML", etc.). Both land on the same module names FeatureData already themes the
// sidebar with, so reuse that registry instead of a parallel one; anything that isn't a
// recognized module (INFO, ALERT, GENERAL, PLATFORM, unknown) falls back to a neutral
// bell/slate treatment.
const CATEGORY_TO_FEATURE: Record<string, FeatureType> = {
	CASE: FeatureType.CASES,
	CASES: FeatureType.CASES,
	AML: FeatureType.AML,
	CFM: FeatureType.FLOWS,
	FLOWS: FeatureType.FLOWS,
	FORMS: FeatureType.FORMS,
	POA: FeatureType.PROOF_OF_ADDRESS,
};

// Legacy/system-generated categories with no product-module equivalent in
// FeatureData — kept as their own bucket (icon + color) rather than folded
// into the generic fallback, since "system" notifications are common enough
// to deserve a distinct, recognizable treatment.
const SYSTEM_CATEGORIES = new Set([
	'SYSTEM',
	'PLATFORM',
	'INFO',
	'ALERT',
	'GENERAL',
	'NOTIFICATION',
]);
const SYSTEM_ICON = 'settings-sliders';
const SYSTEM_COLOR = Color.INFO;

const FALLBACK_ICON = 'bell-notification';
const FALLBACK_COLOR = Color.SLATE;

const featureFor = (category: string): FeatureType | undefined =>
	CATEGORY_TO_FEATURE[(category || '').toUpperCase()];

// Returns a raw icon-name string (matching how FeatureData.icon already stores it,
// e.g. 'workflow-setting') rather than an Icons enum member — packages/shared has no
// dependency on @arritech/assets, and Icon.vue's `name` prop already accepts a plain
// string.
export function notificationCategoryIcon(category: string): string {
	const feature = featureFor(category);
	if (feature) return Feature.icon(feature);
	if (SYSTEM_CATEGORIES.has((category || '').toUpperCase())) return SYSTEM_ICON;
	return FALLBACK_ICON;
}

export function notificationCategoryColor(category: string): Color {
	const feature = featureFor(category);
	if (feature) return Feature.color(feature);
	if (SYSTEM_CATEGORIES.has((category || '').toUpperCase()))
		return SYSTEM_COLOR;
	return FALLBACK_COLOR;
}

// The single source of truth for a category's display label — every caller
// (the filter panel, the row list, the message detail) reads this instead of
// each formatting the raw category string its own way, which is how the list
// and the detail view drifted (one showed "CASE", the other "Cases").
export function notificationCategoryLabelKey(category: string): string {
	const feature = featureFor(category);
	return feature ? Feature.get(feature).titleKey : 'common.features.system';
}

// The finite set of category "buckets" the Inbox filter panel offers —
// derived from the same mapping the icon/color helpers use, so the filter
// options can never drift from what a notification can actually resolve to.
export interface NotificationCategoryOption {
	value: string;
	feature?: FeatureType;
}

export const NOTIFICATION_CATEGORY_OPTIONS: NotificationCategoryOption[] = [
	{ value: 'CASE', feature: FeatureType.CASES },
	{ value: 'AML', feature: FeatureType.AML },
	{ value: 'FLOWS', feature: FeatureType.FLOWS },
	{ value: 'FORMS', feature: FeatureType.FORMS },
	{ value: 'POA', feature: FeatureType.PROOF_OF_ADDRESS },
	{ value: 'SYSTEM' },
];

// Whether a notification's raw category string belongs to a given filter
// bucket's value (both sides normalized/uppercased, and SYSTEM absorbs every
// non-module category rather than requiring an exact string match).
export function notificationMatchesCategory(
	category: string,
	filterValue: string
): boolean {
	const normalizedCategory = (category || '').toUpperCase();
	const normalizedFilter = (filterValue || '').toUpperCase();
	if (normalizedFilter === 'SYSTEM') {
		return !featureFor(normalizedCategory);
	}
	const feature = featureFor(normalizedCategory);
	const filterFeature = featureFor(normalizedFilter);
	return Boolean(feature) && feature === filterFeature;
}
